import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LogOut, RefreshCw, Inbox, Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { LOGO } from "../config/site";

const STATUSES = ["new", "contacted", "in_progress", "closed"];
const STATUS_STYLES = {
  new: "bg-gold-pale text-gold border-gold/30",
  contacted: "bg-sky-50 text-sky-700 border-sky-200",
  in_progress: "bg-magenta-pale text-magenta-dark border-magenta/30",
  closed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const inputCls =
  "w-full rounded-lg border border-slate-200 bg-white px-4 py-3 font-inter text-[13.5px] text-navy placeholder:text-slate-400 transition-[border-color,box-shadow] duration-300 focus:border-gold focus:ring-2 focus:ring-gold/20";

export default function AdminPage() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [data, setData] = useState({ enquiries: [], total: 0 });
  const [loadingList, setLoadingList] = useState(false);
  const [filter, setFilter] = useState("all");

  const loadEnquiries = async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/admin/enquiries");
      setData(data);
    } catch (e) {
      if (e.response?.status === 401) setUser(false);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) loadEnquiries();
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
    } catch (err) {
      setLoginError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const setStatus = async (id, status) => {
    setData((d) => ({ ...d, enquiries: d.enquiries.map((x) => (x.id === id ? { ...x, status } : x)) }));
    await api.patch(`/admin/enquiries/${id}`, { status }).catch(() => loadEnquiries());
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-mist" data-testid="admin-loading">
        <Loader2 className="h-7 w-7 animate-spin text-gold" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy px-5" data-testid="admin-login-page">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-xl border border-white/10 bg-white p-8 shadow-2xl">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="RCS logo" className="h-11 w-11 rounded-full object-cover ring-1 ring-gold/40" />
            <div>
              <p className="font-jakarta text-[15px] font-extrabold text-navy">RCS Admin</p>
              <p className="font-mono2 text-[9px] uppercase tracking-[0.24em] text-slate-500">Enquiries dashboard</p>
            </div>
          </div>
          <form onSubmit={login} className="mt-7 space-y-4" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="mb-1.5 block font-jakarta text-[12px] font-semibold text-navy">Email</label>
              <input id="admin-email" type="email" className={inputCls} data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin email" />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-1.5 block font-jakarta text-[12px] font-semibold text-navy">Password</label>
              <input id="admin-password" type="password" className={inputCls} data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            {loginError && <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[12px] text-red-600" data-testid="admin-login-error">{loginError}</p>}
            <button type="submit" disabled={loggingIn} data-testid="admin-login-submit"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-navy py-3.5 font-jakarta text-sm font-semibold text-white transition-colors duration-300 hover:bg-gold disabled:opacity-70">
              {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {loggingIn ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <a href="/" className="mt-5 block text-center font-inter text-[12px] text-slate-400 hover:text-gold" data-testid="admin-back-home-link">← Back to website</a>
        </motion.div>
      </div>
    );
  }

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: data.enquiries.filter((e) => e.status === s).length }), {});
  const filtered = filter === "all" ? data.enquiries : data.enquiries.filter((e) => e.status === filter);

  return (
    <div className="min-h-screen bg-mist" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="RCS logo" className="h-9 w-9 rounded-full object-cover ring-1 ring-gold/40" />
            <div>
              <p className="font-jakarta text-[14px] font-extrabold text-navy">Enquiries</p>
              <p className="font-mono2 text-[9px] uppercase tracking-[0.22em] text-slate-500">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <button onClick={loadEnquiries} data-testid="admin-refresh-button" className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-gold hover:text-gold" aria-label="Refresh">
              <RefreshCw className={`h-4 w-4 ${loadingList ? "animate-spin" : ""}`} />
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 font-jakarta text-[12px] font-semibold text-white transition-colors hover:bg-gold">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="admin-stats">
          {[{ label: "Total", value: data.total, key: "all" }, ...STATUSES.map((s) => ({ label: s.replace("_", " "), value: counts[s] || 0, key: s }))].map((c) => (
            <button key={c.key} onClick={() => setFilter(c.key)} data-testid={`admin-filter-${c.key}`}
              className={`rounded-xl border p-4 text-left transition-[border-color,box-shadow] duration-300 ${filter === c.key ? "border-gold bg-white shadow-[0_12px_30px_-16px_rgba(204,134,13,0.4)]" : "border-slate-200 bg-white hover:border-gold/50"}`}>
              <p className="font-jakarta text-2xl font-extrabold text-navy">{c.value}</p>
              <p className="mt-0.5 font-mono2 text-[9.5px] uppercase tracking-[0.2em] text-slate-500">{c.label}</p>
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-3">
          {filtered.length === 0 && !loadingList && (
            <div className="flex flex-col items-center rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="admin-empty-state">
              <Inbox className="h-8 w-8 text-slate-300" />
              <p className="mt-3 font-jakarta text-[14px] font-semibold text-navy">No enquiries yet</p>
              <p className="mt-1 font-inter text-[12.5px] text-slate-500">New project enquiries from the website will appear here.</p>
            </div>
          )}
          {filtered.map((e) => (
            <motion.article key={e.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`admin-enquiry-${e.id.slice(0, 8)}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-jakarta text-[15px] font-bold text-navy">{e.full_name}{e.org_name ? <span className="font-medium text-slate-500"> · {e.org_name}</span> : null}</p>
                  <p className="mt-0.5 font-mono2 text-[10.5px] text-slate-500">{e.email} · {e.phone}</p>
                </div>
                <select
                  value={e.status}
                  onChange={(ev) => setStatus(e.id, ev.target.value)}
                  data-testid={`admin-status-select-${e.id.slice(0, 8)}`}
                  className={`cursor-pointer rounded-full border px-3 py-1.5 font-mono2 text-[10.5px] font-semibold uppercase tracking-wider ${STATUS_STYLES[e.status] || ""}`}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-navy px-3 py-1 font-mono2 text-[10px] text-gold-light">{e.service}</span>
                <span className="rounded-full border border-slate-200 px-3 py-1 font-mono2 text-[10px] text-slate-600">{e.budget}</span>
                <span className="rounded-full border border-slate-200 px-3 py-1 font-mono2 text-[10px] text-slate-400">
                  {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </span>
              </div>
              <p className="mt-3 whitespace-pre-line border-t border-slate-100 pt-3 font-inter text-[13px] leading-relaxed text-slate-600">{e.details}</p>
            </motion.article>
          ))}
        </div>
      </main>
    </div>
  );
}
